/**
 * Module: GlobalState
 *
 * Description: This module provides a singleton class that handles global variables.
 * It ensures safe access to the variables, even in the presence of multiple asynchronous processes.
 * The class uses locks and queues to coordinate access to the variables, preventing interference.
 * It supports setting and retrieving global variables of various types.
 *
 * Usage:
 *  - Import the module: import GlobalState from './GlobalState';
 *  - Set global variables: GlobalState.setGlobalVariable<VariableType>('variable1', value);
 *  - Get global variables: const variable1 = GlobalState.getGlobalVariable<VariableType>('variable1');
 *  - The module maintains a single instance, allowing access to the same global variables across the application.
 */

export class GlobalState {
  private static instance: GlobalState;
  private globalVariables: Record<string, unknown>;
  private locks: Record<string, boolean>;
  private queues: Record<string, unknown[]>;

  private constructor() {
    this.globalVariables = {};
    this.locks = {};
    this.queues = {};
  }

  public static get Instance(): GlobalState {
    if (!GlobalState.instance) {
      GlobalState.instance = new GlobalState();
    }
    return GlobalState.instance;
  }

  public async setGlobalVariable<T>(name: string, value: T): Promise<void> {
    if (this.locks[name]) {
      if (!this.queues[name]) {
        this.queues[name] = [];
      }
      this.queues[name].push(value);
    } else {
      this.locks[name] = false;
      this.globalVariables[name] = value;
      await this.processQueue(name);
    }
  }

  private async processQueue<T>(name: string): Promise<void> {
    const queue = this.queues[name];

    if (queue && queue.length > 0) {
      this.locks[name] = true;
      while (queue.length > 0) {
        const value = queue.shift();
        if (value !== undefined) {
          await new Promise<void>(resolve => setTimeout(resolve, 1000));
          this.globalVariables[name] = value as T;
        }
      }
    }

    this.locks[name] = false;

    delete this.queues[name];
  }
  public getGlobalVariable<T>(name: string, default_value?: T): T {
    const value = this.globalVariables[name];
    return (value === undefined ? default_value : value) as T;
  }
}

// Singleton instance
//   const GlobalState: GlobalState = GlobalState.getInstance();
//   Object.freeze(GlobalState);

//   export default GlobalState;
